import { FC, useState, useEffect, useMemo } from "react";
import OrderModal from "./OrderModal";
import { calculateOrderTotal, formatCurrency } from "@/lib/formatCurrency";
import {
  Bike,
  Clock,
  Phone,
  ShoppingBag,
  Store,
  MapPin,
  DollarSign,
  Battery,
  SignalHigh,
  SignalLow,
  Car,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useOrder } from "@/hooks/useOrder";
import { useDriversApi } from "@/hooks/useDriversApi";

interface DriverProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  driver: any;
}

// Sample data to show while loading
const sampleDriverData = {
  image: "/driver-illustration/profile-pic-default.svg",
  name: "Juan Perez",
  worker_type: "full-time",
  vehicle_type: "bicycle",
  branch: { name: "Sucursal" },
  phone: "11 2526 3653",
  device_info: {
    model: "iPhone 14",
    battery_level: 80,
    internet_connection: true,
  },
};

const DriverProfileModal: FC<DriverProfileModalProps> = ({
  isOpen,
  onClose,
  driver,
}) => {
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [driverData, setDriverData] = useState<any>(null);
  const [driverLoading, setDriverLoading] = useState(false);

  // Normalize order IDs: prefer explicit orderId, fallback to orderIds array from realtime
  const orderIds = useMemo(() => {
    const d: any = driver;
    const ids: string[] = [];

    if (d?.orderIds && Array.isArray(d.orderIds)) {
      d.orderIds.forEach((id: any) => {
        if (id !== null && id !== undefined) {
          ids.push(String(id));
        }
      });
    }

    if (ids.length === 0 && d?.orderId !== null && d?.orderId !== undefined) {
      ids.push(String(d.orderId));
    }

    return ids;
  }, [driver]);

  // Index of the currently viewed order (for drivers with multiple active orders)
  const [activeOrderIndex, setActiveOrderIndex] = useState(0);

  // Reset index when driver or orderIds change
  useEffect(() => {
    setActiveOrderIndex(0);
  }, [orderIds.join(",")]);

  const activeOrderId =
    orderIds.length > 0
      ? orderIds[Math.min(activeOrderIndex, orderIds.length - 1)]
      : null;
  const hasMultipleOrders = orderIds.length > 1;

  // Fetch order data if there's an active order
  const { order, loading: orderLoading } = useOrder(activeOrderId);

  const { fetchDriverById } = useDriversApi();

  useEffect(() => {
    if (driver && driver.id) {
      setDriverLoading(true);
      const fetchDriver = async () => {
        try {
          const driverData = await fetchDriverById(driver.id);
          setDriverData(driverData?.driver);
        } finally {
          setDriverLoading(false);
        }
      };
      fetchDriver();
    }
  }, [driver]);

  useEffect(() => {
    // Prevent body scrolling when modal is open
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOrderClick = () => {
    setIsOrderModalOpen(true);
  };

  // Use sample data if loading, otherwise real data
  const dataToShow = driverLoading ? sampleDriverData : driverData;
  const type = dataToShow?.vehicle_type;
  const contractType = (workerType: string) => {
    if (workerType === "full-time") return "Full time";
    if (workerType === "part-time") return "Part time";
    if (workerType === "freelance") return "Freelance";
    return workerType;
  };

  // Show loading overlay if driver data is loading
  const isModalLoading = driverLoading;

  // DRY: Helper to check if last connection is today
  const isLastConnectedToday = (driverDataObj: any) => {
    if (!driverDataObj?.last_time_connected) return false;
    const lastConnected = new Date(driverDataObj.last_time_connected);
    const now = new Date();
    return (
      lastConnected.getFullYear() === now.getFullYear() &&
      lastConnected.getMonth() === now.getMonth() &&
      lastConnected.getDate() === now.getDate()
    );
  };

  // Helper for connection status
  const getConnectionStatus = () => {
    if (driverData?.driver_working && isLastConnectedToday(driverData)) {
      return "Conectado";
    }
    switch (driver?.driver_status) {
      case "available":
        return "Disponible";
      case "unavailable":
        return "No disponible";
      case "pending":
        return "Pendiente";
      case "accepted":
        return "Aceptado";
      case "driver_at_branch":
        return "En sucursal";
      case "sorted":
        return "Surtida";
      case "in_transit":
        return "En tránsito";
      case "on_site":
        return "En sitio";
      case "on_delivery":
        return "En ruta";
      case "completed":
        return "Completado";
      default:
        return "Desconectado";
    }
  };

  // Helper for connection dot color
  const getConnectionDotColor = () => {
    if (driverData?.driver_working && isLastConnectedToday(driverData)) {
      return "bg-green-500";
    }
    // Consider "active" statuses as green, others as gray
    const activeStatuses = [
      "available",
      "pending",
      "accepted",
      "driver_at_branch",
      "sorted",
      "on_delivery",
      "in_transit",
      "on_site",
    ];
    if (activeStatuses.includes(driver?.driver_status)) {
      return "bg-green-500";
    }
    if (driver?.driver_status === "completed") {
      return "bg-blue-500";
    }
    return "bg-gray-500";
  };

  const getDriverStatus = () => {
    if (driver?.driver_working) {
      return "Conectado";
    }
    switch (order?.status) {
      case "completed":
        return "Completado";
      case "driver_at_branch":
        return "En sucursal";
      case "sorted":
        return "Surtida";
      case "in_transit":
        return "En tránsito";
      case "on_site":
        return "En sitio";
      case "pending":
        return "Pendiente";
      default:
        return "En tránsito";
    }
  };
  // Helper for vehicle icon
  const getVehicleIcon = () => {
    if (type === "bicycle" || type === "motorcycle" || type === "moto") {
      return <Bike className="h-5 w-5 text-gray-600" />;
    }
    return <Car className="h-5 w-5 text-gray-600" />;
  };

  // Helper for vehicle label
  const getVehicleLabel = () => {
    if (type === "bicycle") {
      return "Bicicleta";
    }
    if (type === "motorcycle" || type === "moto") {
      return "Motocicleta";
    }
    return "Automóvil";
  };

  // Helper for battery color
  const getBatteryColor = (level: number) => {
    if (level > 20) return "text-green-500";
    if (level > 10) return "text-yellow-500";
    return "text-red-500";
  };

  // Helper for battery level (show 0 if undefined/null)
  const getBatteryLevel = () => {
    const level = dataToShow?.device_info?.battery_level;
    if (typeof level === "number") return level;
    return 0;
  };

  // Helper for device model
  const getDeviceModel = () => {
    return dataToShow?.device_info?.model || "";
  };

  // Helper for internet connection
  const hasInternetConnection = !!dataToShow?.device_info?.internet_connection;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity duration-300"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 pb-4 max-h-[90vh] overflow-hidden">
        {/* Loading Overlay */}
        {isModalLoading && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/80">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-2"></div>
            <span className="text-gray-700 text-sm font-medium">
              Cargando...
            </span>
          </div>
        )}

        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 ${isModalLoading ? "blur-sm pointer-events-none select-none" : ""}`}
        >
          <div className="flex items-center space-x-3">
            <img
              src={
                dataToShow?.picture ||
                "/driver-illustration/profile-pic-default.svg"
              }
              alt={dataToShow?.name || "Driver"}
              className="h-10 w-10 rounded-full object-cover"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-medium text-gray-900">
                {dataToShow?.name || "Juan Perez"}
              </h2>
              <span className="text-xs text-blue-500 px-2 py-0.5 border-blue-500 border rounded-full">
                {contractType(dataToShow?.worker_type)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          className={`px-4 overflow-y-auto max-h-[calc(90vh-80px)] space-y-3 ${isModalLoading ? "blur-sm pointer-events-none select-none" : ""}`}
        >
          {/* Detalles section */}
          <div className="border p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Detalles</h3>

            <div className="space-y-3">
              <div className="flex items-center">
                <div className="w-6 h-6 mr-3 flex items-center justify-center">
                  {getVehicleIcon()}
                </div>
                <span className="text-sm text-gray-800">
                  {getVehicleLabel()}
                </span>
              </div>

              <div className="flex items-center">
                <div className="w-6 h-6 mr-3 flex items-center justify-center">
                  <Store className="h-5 w-5 text-gray-600" />
                </div>
                <span className="text-sm text-gray-800">
                  {dataToShow?.branch?.name || "Sucursal"}
                </span>
              </div>

              <div className="flex items-center">
                <div className="w-6 h-6 mr-3 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-gray-600" />
                </div>
                <span className="text-sm text-gray-800">
                  {contractType(dataToShow?.worker_type)}
                </span>
              </div>

              <div className="flex items-center">
                <div className="w-6 h-6 mr-3 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-gray-600" />
                </div>
                <span className="text-sm text-gray-800">
                  {dataToShow?.phone || "11 2526 3653"}
                </span>
              </div>
            </div>
          </div>

          {/* Órdenes activas section */}
          {activeOrderId && (
            <div className="border p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Órdenes activas
                </h3>
                {hasMultipleOrders && (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <button
                      type="button"
                      onClick={() =>
                        setActiveOrderIndex((prev) => Math.max(0, prev - 1))
                      }
                      disabled={activeOrderIndex === 0}
                      className="p-1 rounded-full disabled:opacity-40 hover:bg-gray-100"
                      aria-label="Orden anterior"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span>
                      {activeOrderIndex + 1} / {orderIds.length}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setActiveOrderIndex((prev) =>
                          Math.min(orderIds.length - 1, prev + 1),
                        )
                      }
                      disabled={activeOrderIndex >= orderIds.length - 1}
                      className="p-1 rounded-full disabled:opacity-40 hover:bg-gray-100"
                      aria-label="Siguiente orden"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {orderLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-sm text-gray-600">
                    Cargando orden...
                  </span>
                </div>
              ) : order ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 mr-3 flex items-center justify-center">
                        <ShoppingBag className="h-5 w-5 text-gray-600" />
                      </div>
                      <span className="text-sm text-gray-800">
                        Orden {order.tracking_id}
                      </span>
                    </div>
                    <button
                      className="text-sm text-blue-600 font-medium"
                      onClick={handleOrderClick}
                    >
                      Ver
                    </button>
                  </div>

                  <div className="flex items-start">
                    <div className="w-6 h-6 mr-3 flex items-center justify-center mt-0.5">
                      <MapPin className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Dirección entrega: </span>
                      <span className="text-gray-600">
                        {(order as any)?.addresses?.full_address ||
                          "Dirección no disponible"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-6 h-6 mr-3 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Estado: </span>
                      <span className="text-gray-600 capitalize">
                        {getDriverStatus()}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-6 h-6 mr-3 flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Total: </span>
                      <span className="text-gray-600">
                        {formatCurrency(
                          calculateOrderTotal(order),
                          order.currency,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500 py-2">
                  Error al cargar los datos de la orden
                </div>
              )}
            </div>
          )}

          {/* No active orders message */}
          {orderIds.length === 0 && (
            <div className="text-sm text-gray-500 text-center py-2 border rounded-lg p-4 bg-gray-50">
              No hay ninguna orden activa
            </div>
          )}

          {/* Información del dispositivo section */}
          <div className="border p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Información del dispositivo
            </h3>

            <div className="flex items-start space-x-4">
              {/* Phone illustration */}
              <div className="flex-shrink-0 w-20">
                <img src="/driver-illustration/iphone-14.svg" alt="iPhone 14" />
              </div>

              {/* Device info */}
              <div className="flex-1 space-y-3">
                {getDeviceModel() && (
                  <p className="text-sm font-medium text-gray-500">
                    Modelo: <span className="">{getDeviceModel()}</span>
                  </p>
                )}

                {/* Battery */}
                <div className="flex items-center gap-2">
                  <Battery
                    className={`h-5 w-5 ${getBatteryColor(getBatteryLevel())}`}
                  />
                  <div className="flex items-center flex-1">
                    <span className="text-sm text-gray-700 mr-2 w-8">
                      {getBatteryLevel()}%
                    </span>
                  </div>
                </div>

                {/* Signal */}
                <div className="flex items-center gap-2">
                  <span
                    className={`${
                      hasInternetConnection ? "text-green-500" : "text-gray-500"
                    } font-semibold`}
                  >
                    {hasInternetConnection ? (
                      <SignalHigh className="h-5 w-5 text-green-500" />
                    ) : (
                      <SignalLow className="h-5 w-5 text-red-500" />
                    )}
                  </span>
                  <span className="text-sm text-gray-700 ">
                    {hasInternetConnection ? "Con señal" : "Sin señal"}
                  </span>
                </div>

                {/* Connection status */}
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <div
                      className={`w-2 h-2 rounded-full ${getConnectionDotColor()}`}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-700 mr-2">
                    {getConnectionStatus()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Order Modal */}
      <OrderModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        orderId={activeOrderId || ""}
        realTime={driver}
      />
    </div>
  );
};

export default DriverProfileModal;
